import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "prestige_cart_v1";

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, plan = "onetime", qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.slug === product.slug && i.plan === plan);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + qty };
        return copy;
      }
      return [
        ...prev,
        {
          slug: product.slug,
          name: product.name,
          image: product.image,
          accent: product.accent,
          plan,
          quantity: qty,
          price_onetime: product.price_onetime,
          price_subscribe: product.price_subscribe,
        },
      ];
    });
    setOpen(true);
  }, []);

  const updateQty = useCallback((slug, plan, delta) => {
    setItems((prev) =>
      prev
        .map((i) =>
          i.slug === slug && i.plan === plan ? { ...i, quantity: i.quantity + delta } : i
        )
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const setPlan = useCallback((slug, oldPlan, newPlan) => {
    setItems((prev) => {
      const target = prev.find((i) => i.slug === slug && i.plan === oldPlan);
      if (!target) return prev;
      const rest = prev.filter((i) => !(i.slug === slug && i.plan === oldPlan));
      const existing = rest.find((i) => i.slug === slug && i.plan === newPlan);
      if (existing) {
        return rest.map((i) =>
          i.slug === slug && i.plan === newPlan
            ? { ...i, quantity: i.quantity + target.quantity }
            : i
        );
      }
      return [...rest, { ...target, plan: newPlan }];
    });
  }, []);

  const removeItem = useCallback((slug, plan) => {
    setItems((prev) => prev.filter((i) => !(i.slug === slug && i.plan === plan)));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const unitPrice = (i) => (i.plan === "subscribe" ? i.price_subscribe : i.price_onetime);
  const subtotal = useMemo(
    () => items.reduce((sum, i) => sum + unitPrice(i) * i.quantity, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = {
    items,
    open,
    setOpen,
    addItem,
    updateQty,
    setPlan,
    removeItem,
    clearCart,
    subtotal,
    count,
    unitPrice,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
